/**
 * Validator for SpendNatureWipe action.
 *
 * Rules (§6.3, §11.1, §14):
 *   1. stateVersion matches.
 *   2. Phase is "marketCheck".
 *   3. Active player has ≥ 1 nature token.
 *   4. `slots` has 1–4 entries, all distinct, each in 0..3.
 *   5. All specified slots are non-empty (have a wildlife token).
 *
 * This is the "selective wipe" nature spend variant (b) from §6.3. The player
 * pays 1 nature token, returns wildlife from chosen slots to bag, draws fresh.
 * The player must still draft after this action.
 *
 * Note: the spec §6.3 says the two nature-spend variants (decouple and
 * selective-wipe) are "mutually exclusive within a single nature-spend" — a
 * player can do EITHER decouple OR wipe (not both from the same spend). However,
 * they can spend MORE than 1 nature token total in a turn. This validator does
 * NOT block a player who has already done SpendNatureDecouple from also doing
 * SpendNatureWipe (that mutual exclusion is a UX convention; the current state
 * model has no `wipeUnlockedThisTurn` flag mirroring `decoupleUnlockedThisTurn`).
 */

import type { SpendNatureWipeAction } from "../types/actions";
import type { ValidationResult } from "../types/common";
import type { GameState } from "../types/game-state";
import type { MarketSlotIndex } from "../types/market";
import { commonGuards, VALID } from "./common-guards";

export const validateSpendNatureWipe = (
  state: GameState,
  action: SpendNatureWipeAction,
): ValidationResult => {
  const guard = commonGuards(state, action.stateVersion, "marketCheck");
  if (guard !== null) {
    return guard;
  }

  const activePlayer = state.players[state.turn.activeSeat];
  if (activePlayer === undefined || activePlayer.natureTokens < 1) {
    return {
      valid: false,
      error: "ERR_NATURE_INSUFFICIENT",
      details: { have: activePlayer?.natureTokens ?? 0, need: 1 },
    };
  }

  // Validate slots array: must be non-empty, 1–4 entries, distinct
  const { slots } = action;
  if (slots.length === 0) {
    return {
      valid: false,
      error: "ERR_INVALID_WIPE_SLOTS",
      details: { slots, reason: "empty" },
    };
  }

  if (slots.length > 4) {
    return {
      valid: false,
      error: "ERR_INVALID_WIPE_SLOTS",
      details: { slots, reason: "too_many" },
    };
  }

  const seen = new Set<number>();
  for (const slot of slots) {
    if (seen.has(slot)) {
      return {
        valid: false,
        error: "ERR_INVALID_WIPE_SLOTS",
        details: { slots, reason: "duplicate", duplicate: slot },
      };
    }
    seen.add(slot);
  }

  // All specified slots must have a wildlife token
  for (const slot of slots) {
    const marketSlot = state.market.slots[slot as MarketSlotIndex];
    if (marketSlot.tokenInstanceId === null) {
      return {
        valid: false,
        error: "ERR_MARKET_SLOT_EMPTY",
        details: { slot, missing: "wildlife" },
      };
    }
  }

  return VALID;
};
